import { apiFetch, post } from "./api";
import type { PagedResult } from "./clients-accounts";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/Quotes/*.cs)
//
// Wire-format casing: ASP.NET Core System.Text.Json default = camelCase.
// Type adaptations:
//   - decimal → number.
//   - DateTime / DateTime? → string (ISO 8601 from JSON).
//   - DateOnly / DateOnly? → string (ISO date from JSON).
// ============================================================================

export type QuoteStatus = "Draft" | "Sent" | "Approved" | "Converted" | "Expired" | "Rejected";

// ----------------------------------------------------------------------------
// Quote list / detail
// ----------------------------------------------------------------------------

export interface QuoteListItemDto {
  quoteId: number;
  quoteRef: string;
  status: QuoteStatus;
  description: string | null;
  clientId: number | null;
  clientName: string | null;
  ownerEmployeeId: number | null;
  ownerEmployeeName: string | null;
  branchId: number | null;
  totalIncGst: number | null;
  validUntil: string | null;
  quoteVersionNo: number;
  createdOn: string | null;
  enquiryId: number | null;
}

export interface QuoteDetailDto {
  quoteId: number;
  quoteRef: string;
  status: QuoteStatus;
  clientId: number | null;
  clientName: string | null;
  ownerEmployeeId: number | null;
  ownerEmployeeName: string | null;
  branchId: number | null;
  approvalTypeCode: string | null;
  quoteTemplateId: number | null;
  enquiryId: number | null;
  description: string | null;
  totalExGst: number | null;
  gst: number | null;
  totalIncGst: number | null;
  validUntil: string | null;
  quoteVersionNo: number;
  rowVersionBase64: string | null;
  convertedToJobId: number | null;
}

// ----------------------------------------------------------------------------
// Quote items
// ----------------------------------------------------------------------------

export interface QuoteItemDto {
  quoteItemId: number;
  quoteId: number;
  sortOrder: number;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  lineTotalExGst: number;
  notes: string | null;
  sourceBomId: number | null;
  rowVersionBase64: string | null;
}

export interface CreateQuoteItemRequest {
  sortOrder?: number | null;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  notes?: string | null;
}

export interface UpdateQuoteItemRequest {
  sortOrder: number;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  notes: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Quote options
// ----------------------------------------------------------------------------

export interface QuoteOptionDto {
  quoteOptionId: number;
  quoteId: number;
  sortOrder: number;
  description: string;
  priceDelta: number | null;
  isSelectedDefault: boolean;
  rowVersionBase64: string | null;
}

export interface CreateQuoteOptionRequest {
  description: string;
  priceDelta?: number | null;
  isSelectedDefault?: boolean;
}

export interface UpdateQuoteOptionRequest {
  description: string;
  priceDelta: number | null;
  isSelectedDefault: boolean;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Quote includes
// ----------------------------------------------------------------------------

export interface QuoteIncludeDto {
  quoteIncludeId: number;
  quoteId: number;
  sortOrder: number;
  description: string;
  included: boolean;
  rowVersionBase64: string | null;
}

export interface CreateQuoteIncludeRequest {
  description: string;
  included?: boolean;
}

export interface UpdateQuoteIncludeRequest {
  description: string;
  included: boolean;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Quote T&Cs
// ----------------------------------------------------------------------------

export interface QuoteTandCsDto {
  tandCsText: string | null;
  quoteTandCsTemplateId: number | null;
}

export interface SetQuoteTandCsRequest {
  tandCsText: string;
  quoteTandCsTemplateId?: number | null;
}

// ----------------------------------------------------------------------------
// Quote create / update
// ----------------------------------------------------------------------------

export interface CreateQuoteRequest {
  clientId?: number | null;
  ownerEmployeeId?: number | null;
  branchId?: number | null;
  description?: string | null;
  approvalTypeCode?: string | null;
  quoteTemplateId?: number | null;
  enquiryId?: number | null;
}

export interface UpdateQuoteRequest {
  clientId?: number | null;
  ownerEmployeeId?: number | null;
  branchId?: number | null;
  description?: string | null;
  approvalTypeCode?: string | null;
  quoteTemplateId?: number | null;
  validUntil?: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Status transitions
// ----------------------------------------------------------------------------

export interface ApproveQuoteRequest {
  approverEmployeeId: number;
  isApproveAny: boolean;
  rowVersionBase64: string;
}

export interface RejectQuoteRequest {
  reason: string;
  rejecterEmployeeId: number;
  rowVersionBase64: string;
}

export interface RecallQuoteRequest {
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Reorder
// ----------------------------------------------------------------------------

export interface ReorderQuoteItemsRequest {
  order: { quoteItemId: number; sortOrder: number }[];
}

// ----------------------------------------------------------------------------
// List query params
// ----------------------------------------------------------------------------

export interface ListQuotesParams {
  status?: QuoteStatus | QuoteStatus[];
  clientId?: number;
  ownerEmployeeId?: number;
  enquiryId?: number;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Convenience type aliases used by SPA components
// ============================================================================

export type QuoteListItem = QuoteListItemDto;
export type QuoteDetail = QuoteDetailDto;
export type QuoteItem = QuoteItemDto;
export type QuoteOption = QuoteOptionDto;
export type QuoteInclude = QuoteIncludeDto;

// ============================================================================
// Query-string helper
// ============================================================================

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

// ============================================================================
// API helpers
// ============================================================================

export const quotesApi = {
  // ---- Quotes -----------------------------------------------------------------

  list: (params: ListQuotesParams = {}): Promise<PagedResult<QuoteListItemDto>> => {
    const search = qs(params as Record<string, unknown>);
    return apiFetch<PagedResult<QuoteListItemDto>>(`/api/quotes${search ? `?${search}` : ""}`);
  },

  get: (id: number): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>(`/api/quotes/${id}`),

  create: (body: CreateQuoteRequest): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>("/api/quotes", { method: "POST", body: JSON.stringify(body) }),

  patch: (id: number, body: UpdateQuoteRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  delete: (id: number): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}`, { method: "DELETE" }),

  duplicate: (id: number): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>(`/api/quotes/${id}/duplicate`, { method: "POST" }),

  // ---- Quote items ------------------------------------------------------------

  items: (id: number): Promise<QuoteItemDto[]> =>
    apiFetch<QuoteItemDto[]>(`/api/quotes/${id}/items`),

  addItem: (id: number, body: CreateQuoteItemRequest): Promise<QuoteItemDto> =>
    apiFetch<QuoteItemDto>(`/api/quotes/${id}/items`, { method: "POST", body: JSON.stringify(body) }),

  patchItem: (id: number, itemId: number, body: UpdateQuoteItemRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteItem: (id: number, itemId: number): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/items/${itemId}`, { method: "DELETE" }),

  reorderItems: (id: number, body: ReorderQuoteItemsRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/items/reorder`, { method: "POST", body: JSON.stringify(body) }),

  // ---- Quote options ----------------------------------------------------------

  options: (id: number): Promise<QuoteOptionDto[]> =>
    apiFetch<QuoteOptionDto[]>(`/api/quotes/${id}/options`),

  addOption: (id: number, body: CreateQuoteOptionRequest): Promise<QuoteOptionDto> =>
    apiFetch<QuoteOptionDto>(`/api/quotes/${id}/options`, { method: "POST", body: JSON.stringify(body) }),

  patchOption: (id: number, optId: number, body: UpdateQuoteOptionRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/options/${optId}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteOption: (id: number, optId: number): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/options/${optId}`, { method: "DELETE" }),

  // ---- Quote includes ---------------------------------------------------------

  includes: (id: number): Promise<QuoteIncludeDto[]> =>
    apiFetch<QuoteIncludeDto[]>(`/api/quotes/${id}/includes`),

  addInclude: (id: number, body: CreateQuoteIncludeRequest): Promise<QuoteIncludeDto> =>
    apiFetch<QuoteIncludeDto>(`/api/quotes/${id}/includes`, { method: "POST", body: JSON.stringify(body) }),

  patchInclude: (id: number, incId: number, body: UpdateQuoteIncludeRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/includes/${incId}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteInclude: (id: number, incId: number): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/includes/${incId}`, { method: "DELETE" }),

  // ---- T&Cs -------------------------------------------------------------------

  getTandCs: (id: number): Promise<QuoteTandCsDto> =>
    apiFetch<QuoteTandCsDto>(`/api/quotes/${id}/tandcs`),

  setTandCs: (id: number, body: SetQuoteTandCsRequest): Promise<void> =>
    apiFetch<void>(`/api/quotes/${id}/tandcs`, { method: "PUT", body: JSON.stringify(body) }),

  // ---- Status transitions -----------------------------------------------------

  approve: (id: number, body: ApproveQuoteRequest): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>(`/api/quotes/${id}/approve`, { method: "POST", body: JSON.stringify(body) }),

  reject: (id: number, body: RejectQuoteRequest): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>(`/api/quotes/${id}/reject`, { method: "POST", body: JSON.stringify(body) }),

  recall: (id: number, body: RecallQuoteRequest): Promise<QuoteDetailDto> =>
    apiFetch<QuoteDetailDto>(`/api/quotes/${id}/recall`, { method: "POST", body: JSON.stringify(body) }),
};

// ============================================================================
// Plan II — send / resend / pdf
// ============================================================================

export type SendQuoteRequest = { customerEmail: string; fromEmployeeName?: string };
export type QuoteSentResult = {
  quoteId: number;
  quoteRef: string;
  version: number;
  linkId: number;
  approvalLinkExpiresUtc: string;
  pdfBlobPath: string;
};

export const sendQuote   = (id: number, body: SendQuoteRequest): Promise<QuoteSentResult> =>
  post<QuoteSentResult>(`/api/quotes/${id}/send`, body);

export const resendQuote = (id: number, body: SendQuoteRequest): Promise<QuoteSentResult> =>
  post<QuoteSentResult>(`/api/quotes/${id}/resend`, body);

/** Returns a URL string suitable for direct anchor href or iframe src. Does not fetch. */
export const getQuotePdfUrl = (id: number): string => `/api/quotes/${id}/pdf`;
